import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";
import { FileText, Upload, X } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
} from "@/components/ui/attachment";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { SectionCard } from "components/BaseForm";

const FILE_INPUT_ID = "revenue-opportunity-attachments";

const UPLOAD_STATES = { finished: "done", failed: "error" };

const uploadDescription = upload => {
  if (upload.status === "failed") return `Upload failed: ${upload.error}`;
  if (upload.status === "finished") return "Uploaded";
  return `Uploading ${upload.percent}%`;
};

const SavedAttachments = ({ title }) => {
  const { setValue } = useFormContext();
  const attachments = useWatch({ name: "attachments" });
  if (attachments.length === 0) return null;

  const remove = id =>
    setValue(
      "attachments",
      attachments.filter(attachment => attachment.id !== id),
      { shouldDirty: true }
    );

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <span className="tw:text-sm tw:font-medium tw:text-foreground">
        {title}
      </span>
      <AttachmentGroup>
        {attachments.map(attachment => (
          <Attachment key={attachment.id}>
            <AttachmentMedia>
              <FileText />
            </AttachmentMedia>
            <AttachmentContent>
              <AttachmentTitle>
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:hover:underline"
                >
                  {attachment.file_name}
                </a>
              </AttachmentTitle>
            </AttachmentContent>
            <AttachmentActions>
              <AttachmentAction
                aria-label={`Remove ${attachment.file_name}`}
                onClick={() => remove(attachment.id)}
              >
                <X />
              </AttachmentAction>
            </AttachmentActions>
          </Attachment>
        ))}
      </AttachmentGroup>
    </div>
  );
};

SavedAttachments.propTypes = {
  title: PropTypes.string.isRequired,
};

const AttachmentsSection = ({ savedTitle, attachmentUploads }) => {
  const { uploads, addFiles, removeUpload } = attachmentUploads;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
  });

  return (
    <SectionCard title="Attachments">
      <SavedAttachments title={savedTitle} />
      {uploads.length > 0 && (
        <AttachmentGroup>
          {uploads.map(upload => (
            <Attachment
              key={upload.id}
              state={UPLOAD_STATES[upload.status] ?? "uploading"}
            >
              <AttachmentMedia>
                {UPLOAD_STATES[upload.status] ? <FileText /> : <Spinner />}
              </AttachmentMedia>
              <AttachmentContent>
                <AttachmentTitle>{upload.file.name}</AttachmentTitle>
                <AttachmentDescription>
                  {uploadDescription(upload)}
                </AttachmentDescription>
              </AttachmentContent>
              <AttachmentActions>
                <AttachmentAction
                  aria-label={`Remove ${upload.file.name}`}
                  onClick={() => removeUpload(upload)}
                >
                  <X />
                </AttachmentAction>
              </AttachmentActions>
            </Attachment>
          ))}
        </AttachmentGroup>
      )}
      <div
        {...getRootProps({
          className: cn(
            "tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:gap-2 tw:rounded-lg tw:border-2 tw:border-dashed tw:border-(--color-border) tw:bg-(--color-surface) tw:p-6 tw:text-center tw:text-sm tw:text-(--color-text-secondary) tw:transition-colors tw:hover:border-(--color-highlight)",
            isDragActive && "tw:border-(--color-highlight)"
          ),
        })}
      >
        <Input {...getInputProps({ id: FILE_INPUT_ID })} />
        <Upload className="tw:size-6" />
        <span>Drag and drop files here, or click to select files</span>
      </div>
    </SectionCard>
  );
};

AttachmentsSection.propTypes = {
  savedTitle: PropTypes.string.isRequired,
  attachmentUploads: PropTypes.shape({
    uploads: PropTypes.array.isRequired,
    addFiles: PropTypes.func.isRequired,
    removeUpload: PropTypes.func.isRequired,
  }).isRequired,
};

export default AttachmentsSection;
